import React, { useCallback, useContext, useEffect, useState } from 'react'
import { FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SortDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridColumnProps,
  GridHeaderSelectionChangeEvent,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { UpdateClientStatus } from 'api/Clients/Types/UpdateClientStatus'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { TicketDto, TicketFilterKey } from 'api/Tickets/Types/TicketDto'
import { TicketStatusesIds } from 'api/Tickets/Types/TicketStatus'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import { errorToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridState } from 'features/DataGrid'
import { ChevronDown } from 'lucide-react'
import { normalizeTicketsSortQuery } from 'routes/Tickets/Tickets/normalizer'
import { ticketsColumn } from 'routes/Tickets/ticketsColumn'
import {
  getTicketsViewType,
  getTicketViewTypeWithLabelAndPermission,
  TicketsViewType,
} from 'routes/Tickets/TicketsViewType'
import { useAppDispatch } from 'store'
import { pageableSettings } from 'store/grid/grid.slice'
import { completeTicketsAction } from 'store/tickets/tickets.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { TicketsApiClient } from 'utils/api/ticketsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from 'utils/api/useFetchOneWithFetchingUpdate'
import { AuthSessionContext } from 'utils/AuthContext'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery, FilterLogic } from 'utils/QueryHelpers'

import {
  SelectTicketEvent,
  SelectTicketsAssignTable,
} from '../../../Tickets/Tickets/SelectTicketsAssignTable'
import { ClientNavigation, ClientTab } from '../Navigation/ClientNavigation'
import { clientDetailsTicketPaymentsColumn } from './clientDetailsTicketPaymentsColumn'
import { clientDetailsTicketTranslationColumn } from './clientDetailsTicketTranslationColumn'

interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

interface Props {
  data: ClientDetailedDto
  updateClientStatus(values: UpdateClientStatus): void
}

const ClientDetailsTicket: React.FC<Props> = ({ data, updateClientStatus }) => {
  const [auth] = useContext(AuthSessionContext)
  const ticketsApiClient = useApiClient(TicketsApiClient)
  const permissions = auth?.permissions || []
  const [isAssignUsersModalOpen, setAssignUsersModalOpen] = useState<boolean>(false)

  const [columns, setColumns] = useState<GridColumn[]>([])
  const [ticketViewType, setTicketSelectedViewType] = useState<TicketsViewType | undefined>(
    undefined
  )
  const [accessedTicketsViewType, setTicketsView] = useState<
    { id: TicketsViewType; name: string; permission?: PermissionsEnum }[]
  >([])

  const [clientTickets, setClientTickets] = useState<TicketDto[]>([])
  const setToast = useContext(ToastContext)

  const dispatch = useAppDispatch()

  const { gridState, gridActions } = useGridState()
  const gridFilterState = createGridState(getPaginationInitialState(), {})

  const { callback, forceRefresh: forceRefreshTickets } = useCallbackWithForceRefresh(() => {
    const selectedTicketView = getSelectedTicketView()
    const searchQuery = getQuery(selectedTicketView)
    return ticketsApiClient.getTickets(selectedTicketView, {
      search: searchQuery,
      pageIndex: gridFilterState.pageIndex,
      pageSize: gridFilterState.pageSize,
    })
  }, [])
  const { data: result, isLoading, isFetching } = useFetchOneWithFetchingUpdate(callback)

  useEffect(() => {
    if (result) {
      setSortedTickets(result.items)
      gridActions.setGridFilters({
        ...gridFilterState,
        total: result.totalResults,
      })
    }
  }, [result]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const allowedViewTypes = getTicketsView()
    const allowedViewType = getSelectedTicketView()

    if (isEmpty(columns)) {
      setColumns(getColumnsByTicketViewType(allowedViewType))
    }
    if (!isEmpty(allowedViewTypes)) {
      setTicketsView(allowedViewTypes)
    }
    if (!isEmpty(allowedViewType)) {
      setTicketSelectedViewType(allowedViewType)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getTicketsView = () => {
    if (permissions) {
      return getTicketViewTypeWithLabelAndPermission().filter((viewType) =>
        hasPermission(permissions, viewType.permission)
      )
    }
    return []
  }

  const handleAssignUserModalOpen = () => {
    setAssignUsersModalOpen(true)
  }

  const handleAssignUsersModalClose = () => {
    setAssignUsersModalOpen(false)
  }

  const getSelectedTicketView = () => {
    const [allowedViewType] = getTicketsView()
    return ticketViewType || allowedViewType.id
  }

  const getQuery = useCallback(
    (selectedTicketView: TicketsViewType) => {
      const selectedTicketQueryFilters = getSelectedTicketQueryFilters(selectedTicketView)
      return (
        buildQuery({
          logic: FilterLogic.AND,
          filters: selectedTicketQueryFilters,
        }) ?? null
      )
    },
    [gridState.filter] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const getSelectedTicketQueryFilters = (selectedTicketView: TicketsViewType) => {
    const allowedViewTypes = getTicketsView().filter(
      (ticketType) => ticketType.id !== TicketsViewType.MyTickets
    )
    if (selectedTicketView === TicketsViewType.MyTickets) {
      return [
        {
          field: TicketFilterKey.CLIENT,
          operator: '==',
          logic: FilterLogic.AND,
          value: data.id,
        },
        {
          field: TicketFilterKey.VIEWTYPE,
          operator: '==',
          logic: FilterLogic.AND,
          value: allowedViewTypes.map((x) => x.id),
        },
      ]
    }

    return [
      {
        field: TicketFilterKey.CLIENT,
        operator: '==',
        logic: FilterLogic.AND,
        value: data.id,
      },
    ]
  }

  const setSortedTickets = (ticketsToSort: TicketDto[]) => {
    setClientTickets([...ticketsToSort].sort((a, b) => b.number - a.number))
  }

  const getSelectedIds = () => {
    if (!clientTickets) return []
    return clientTickets
      .filter((t) => t.status.id !== TicketStatusesIds.CLOSED)
      .filter((ticket) => ticket.selected)
      .map((x) => x.id)
  }

  const completeTickets = () => {
    const ids = getSelectedIds()
    if (ids && ticketViewType) {
      dispatch(
        completeTicketsAction(ticketsApiClient, ids, ticketViewType, setToast, forceRefreshTickets)
      )
      unSelectAll()
    }
  }

  const acquireTickets = async () => {
    if (auth && ticketViewType) {
      const ids = clientTickets
        .filter((ticket) => ticket.selected)
        .filter((t) => !t.assignee)
        .filter((t) => t.status.id !== TicketStatusesIds.CLOSED)
        .map((t) => t.id)
      if (isEmpty(ids)) {
        return setToast(
          errorToast(
            'Cannot acquire these tickets. Please select Tickets not Closed and not assigned to you.'
          )
        )
      }
      await ticketsApiClient.acquireTickets({
        ticketIds: ids,
        viewTypeId: ticketViewType,
        assigneeId: auth.user.id,
      })
      setToast({ type: 'success', title: 'Tickets acquired successfully.' })
      forceRefreshTickets()
      unSelectAll()
    }
  }

  const unSelectAll = () =>
    setSortedTickets(clientTickets.map((ticket) => ({ ...ticket, selected: false })))

  const selectionChange = (event: GridSelectionChangeEvent) => {
    const ticketsToSelect = (clientTickets || []).map((item) => {
      if (item.id === event.dataItem.id) {
        item.selected = !event.dataItem.selected
      }
      return item
    })
    setSortedTickets(ticketsToSelect)
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    const ticketsToSelect = (clientTickets || []).map((item) => {
      item.selected = isChecked
      return item
    })
    setSortedTickets(ticketsToSelect)
  }

  const handleGridPageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip,
      recordsPerPage: take,
    })

    const sessionPageSize = sessionStorage.getItem('itemsPerPage')
    if (event.page.take.toString() !== sessionPageSize)
      sessionStorage.setItem('itemsPerPage', event.page.take.toString())

    const selectedTicketView = getSelectedTicketView()
    const searchQuery = getQuery(selectedTicketView)
    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()

    gridActions.setGridFilters({
      take,
      skip,
      pageIndex,
    })

    if (ticketViewType) {
      const response = await ticketsApiClient.getTickets(ticketViewType, {
        search: searchQuery,
        pageIndex,
        pageSize,
      })
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
      if (response?.items) {
        setClientTickets(response.items)
      }
    }
  }

  const handleUserAssign = async (event: SelectTicketEvent) => {
    const userId = event.value.id
    const ids = getSelectedIds()
    if (!ids.length) {
      return setToast(errorToast('Please select tickets to assign.'))
    }
    await ticketsApiClient.assigneeClientToTickets({
      assigneeId: userId,
      ticketIds: getSelectedIds() as string[],
    })
    setAssignUsersModalOpen(false)
    setToast({ type: 'success', title: 'Tickets assigned successfully.' })
    forceRefreshTickets()
    unSelectAll()
  }

  const changeTicketViewType = (ticketsViewType: string | number) => {
    const viewType = getTicketsViewType(ticketsViewType)
    if (viewType) {
      setTicketSelectedViewType(viewType)
      setColumns(getColumnsByTicketViewType(viewType))
    } else {
      setTicketSelectedViewType(undefined)
      setClientTickets([])
      setColumns([])
    }
    forceRefreshTickets()
  }

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeTicketsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })
    const selectedTicketView = getSelectedTicketView()
    const searchQuery = getQuery(selectedTicketView)
    const sortQuery = getSort(event.sort)

    if (ticketViewType) {
      const response = await ticketsApiClient.getTickets(ticketViewType, {
        search: searchQuery,
        sort: sortQuery,
        pageIndex: gridState.pageIndex,
        pageSize: gridState.take,
      })
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
      if (response?.items) {
        setClientTickets(response.items)
      }
    }
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {isAssignUsersModalOpen && (
        <Dialog
          open={isAssignUsersModalOpen}
          onOpenChange={(open) => !open && handleAssignUsersModalClose()}
        >
          <SelectTicketsAssignTable
            departments={clientTickets.length ? clientTickets[0].departments : []}
            onSingleSelect={handleUserAssign}
            selectText='Assign'
          />
        </Dialog>
      )}
      <div>
        <ClientNavigation
          isEdit={false}
          data={data}
          tab={ClientTab.TICKETS}
          updateClientStatus={updateClientStatus}
        />
        <div className='flex justify-between items-center'>
          <FormSelectField
            className='max-w-xs py-2'
            value={ticketViewType}
            onChange={changeTicketViewType}
            options={accessedTicketsViewType}
            label='Ticket Type'
          />
          <DropdownMenu>
            <DropdownMenuTrigger render={<Button type='button' />}>
              Actions <ChevronDown />
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                onSelect={completeTickets}
                disabled={!hasPermission(permissions, PermissionsEnum.CanAcquireTickets)}
              >
                Complete selected tickets
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={acquireTickets}
                disabled={!hasPermission(permissions, PermissionsEnum.CanCompleteTickets)}
              >
                Acquire selected tickets
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={handleAssignUserModalOpen}
                disabled={!hasPermission(permissions, PermissionsEnum.CanAssignTickets)}
              >
                Assign selected tickets
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <Loading isLoading={isFetching}>
          <Grid
            data={clientTickets}
            sort={gridState.sort}
            total={gridState.total}
            sortable={{
              allowUnsort: true,
              mode: 'multiple',
            }}
            take={gridState.take}
            skip={gridState.skip}
            selectedField='selected'
            onSelectionChange={selectionChange}
            onSortChange={handleSortChange}
            onHeaderSelectionChange={handleHeaderSelectionChange}
            onPageChange={handleGridPageChange}
            pageable={pageableSettings}
            resizable
          >
            <Column
              field='selected'
              width='50px'
              headerSelectionValue={
                (clientTickets || []).findIndex((dataItem) => !dataItem.selected) === -1
              }
            />
            {columns
              .filter((c) => c.show !== false)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
          </Grid>
        </Loading>
      </div>
    </React.Fragment>
  )
}

const getColumnsByTicketViewType = (ticketsViewType: TicketsViewType) => {
  if (ticketsViewType === TicketsViewType.Translation) {
    return clientDetailsTicketTranslationColumn
  }
  if (ticketsViewType === TicketsViewType.Payments) {
    return clientDetailsTicketPaymentsColumn
  }
  return ticketsColumn
}

export default ClientDetailsTicket
